<template>
  <div class="home min-h-screen bg-gray-50 dark:bg-gray-900">
    <main class="container mx-auto px-4 py-8">
      <!-- Mobile Tag Filter Button -->
      <div class="md:hidden mb-6">
        <button 
          @click="showMobileTags = !showMobileTags" 
          class="w-full flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-gray-800 dark:text-gray-200"
        >
          <span class="text-lg font-semibold">{{ selectedTag ? `Tag: ${selectedTag}` : 'Filter by Tags' }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :class="{'transform rotate-180': showMobileTags}" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
        
        <!-- Mobile Tags Dropdown -->
        <div v-if="showMobileTags" class="mt-2 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div v-if="loadingTags" class="py-2 text-center">
            <p class="text-gray-500">Loading tags...</p>
          </div>
          <div v-else-if="tags.length === 0" class="py-2 text-center">
            <p class="text-gray-500">No tags found</p>
          </div>
          <div v-else class="grid grid-cols-2 gap-2">
            <button
              v-for="tag in sortedTags"
              :key="tag.name"
              @click="filterByTag(tag.name)"
              :class="[
                'text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 flex justify-between items-center',
                selectedTag === tag.name
                  ? 'border border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-900/50' // Selected Outline
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white' // Unselected Outline
              ]"
            >
              <span class="truncate">{{ tag.name }}</span>
              <span class="text-xs">{{ tag.count }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col md:flex-row gap-8">
        <!-- Desktop Sidebar -->
        <aside class="hidden md:block md:w-64 flex-shrink-0 sticky top-24 self-start">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">タグ</h2>
            <div v-if="loadingTags" class="py-4 text-center">
              <p class="text-gray-500">Loading tags...</p>
            </div>
            <div v-else-if="tags.length === 0" class="py-4 text-center">
              <p class="text-gray-500">No tags found</p>
            </div>
            <div v-else class="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              <button
                v-for="tag in sortedTags"
                :key="tag.name"
                @click="filterByTag(tag.name)"
                :class="[
                  'w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200',
                  selectedTag === tag.name
                    ? 'border border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-900/50' // Selected Outline
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white' // Unselected Outline
                ]"
              >
                {{ tag.name }}
                <span class="float-right text-xs">{{ tag.count }}</span>
              </button>
            </div>
          </div>
        </aside>

        <!-- メインコンテンツ -->
        <div class="flex-1" ref="postsContainer">
          <div v-if="loading && !loadingMore" class="text-center py-8">
            <p class="text-gray-600 dark:text-gray-400">Loading posts...</p>
          </div>
          <div v-else-if="error" class="text-center py-8">
            <p class="text-red-500">{{ error }}</p>
          </div>
          <template v-else>
            <section class="mb-12">
              <h2 class="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Featured Posts</h2>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                <BlogCard v-for="post in topPosts" :key="post.id" :post="post" />
              </div>
            </section>

            <section>
              <h2 class="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">All Posts</h2>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                <!-- Iterate over allPosts directly -->
                <BlogCard v-for="post in allPosts" :key="post.id" :post="post" /> 
              </div>
            </section>

            <!-- Loading Spinner for Eager Loading -->
            <div v-if="loadingMore" class="flex justify-center items-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <p class="ml-3 text-gray-600 dark:text-gray-400">Loading more posts...</p>
            </div>
            
            <div v-if="!loadingMore && pagination.totalPages > 1" class="mt-8 flex flex-wrap justify-center gap-2">
              <button
                v-for="pageNumber in pagination.totalPages"
                :key="pageNumber"
                @click="goToPage(pageNumber)"
                :class="[
                  'px-3 py-1 rounded-full transition-colors duration-200', // Added transition
                  currentPage === pageNumber
                    ? 'border border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-900/50' // Current Page Outline
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white' // Other Page Outline
                ]"
              >
                {{ pageNumber }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import BlogCard from '@/components/BlogCard.vue'

export default {
  name: 'Home',
  components: {
    BlogCard
  },
  setup() {
    const store = useStore()
    const currentPage = ref(1)
    const postsPerPage = ref(20)
    const selectedTag = ref(null)
    const loadingTags = ref(false)
    const showMobileTags = ref(false)
    const loadingMore = ref(false)
    const postsContainer = ref(null)
    const allPosts = ref([])
    const topPostsList = ref([])
    const pagination = ref({
      total: 0,
      page: 1,
      perPage: 20,
      totalPages: 0
    })

    const tags = computed(() => store.state.tags)
    const loading = computed(() => store.state.loading)
    const error = computed(() => store.state.error)

    const sortedTags = computed(() => {
      return [...tags.value].sort((a, b) => b.count - a.count)
    })

    const topPosts = computed(() => {
      return topPostsList.value
    })

    // Remove filteredPosts computed property as filtering is now done by the backend
    // const filteredPosts = computed(() => {
    //   if (!selectedTag.value) {
    //     return allPosts.value
    //   }
    //   return allPosts.value.filter(post => post.tags && post.tags.includes(selectedTag.value))
    // })

    const filterByTag = (tagName) => {
      selectedTag.value = selectedTag.value === tagName ? null : tagName
      currentPage.value = 1
      allPosts.value = []
      // Pass the selected tag to fetchPosts
      fetchPosts(false, selectedTag.value)
    }

    const goToPage = (page) => {
      currentPage.value = page
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    // Intersection Observer for infinite scroll
    let observer = null
    const setupIntersectionObserver = () => {
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(handleIntersection, {
          root: null,
          rootMargin: '0px',
          threshold: 0.1
        })
        
        // Create a sentinel element to observe
        const sentinel = document.createElement('div')
        sentinel.id = 'sentinel'
        sentinel.style.height = '10px'
        if (postsContainer.value) {
          postsContainer.value.appendChild(sentinel)
          observer.observe(sentinel)
        }
      }
    }

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !loadingMore.value && currentPage.value < pagination.value.totalPages) {
          loadMorePosts()
        }
      })
    }

    const loadMorePosts = async () => {
      if (loadingMore.value) return
      
      loadingMore.value = true
      currentPage.value++
      
      try {
        // Add a 2-second delay before fetching
        await new Promise(resolve => setTimeout(resolve, 2000)); 
        // Pass the current tag when loading more
        await fetchPosts(true, selectedTag.value) 
      } catch (err) {
        console.error('Error loading more posts:', err)
      } finally {
        loadingMore.value = false
      }
    }

    onMounted(async () => {
      loadingTags.value = true
      try {
        await store.dispatch('fetchTags')
      } catch (error) {
        console.error('Error fetching tags:', error)
      } finally {
        loadingTags.value = false
      }
      
      // Pass null for tag on initial load
      await fetchPosts(false, null) 
      
      nextTick(() => {
        setupIntersectionObserver()
      })
    })

    onUnmounted(() => {
      if (observer) {
        observer.disconnect()
      }
    })

    // Modify fetchPosts to accept a tag parameter
    async function fetchPosts(append = false, tag = null) { 
      try {
        if (!append) {
          store.commit('setLoading', true)
        }
        
        // Pass the tag to the store action
        const response = await store.dispatch('fetchPosts', { 
          page: currentPage.value, 
          perPage: postsPerPage.value,
          includeFeatured: false, // Keep this false for now, assuming backend handles featured logic based on tag
          tag: tag // Add the tag parameter here
        })
        
        if (response) {
          // Check if we got an array (old format) or object with posts/topPosts (new format)
          if (Array.isArray(response)) {
            if (!append) {
              allPosts.value = response
              topPostsList.value = []
              pagination.value = {
                total: response.length,
                page: 1,
                perPage: response.length,
                totalPages: 1
              }
            } else {
              // Append new posts to existing list
              allPosts.value = [...allPosts.value, ...response]
            }
          } else {
            if (!append) {
              allPosts.value = response.posts || []
              topPostsList.value = response.topPosts || []
              pagination.value = response.pagination || {
                total: 0,
                page: 1,
                perPage: 20,
                totalPages: 0
              }
            } else {
              // Append new posts to existing list
              allPosts.value = [...allPosts.value, ...(response.posts || [])]
              pagination.value = response.pagination || pagination.value
            }
          }
        } else {
          console.log('No response from API')
          if (!append) {
            allPosts.value = []
            topPostsList.value = []
            pagination.value = {
              total: 0,
              page: 1,
              perPage: 20,
              totalPages: 0
            }
          }
        }
      } catch (err) {
        console.error('Error fetching posts:', err)
      } finally {
        if (!append) {
          store.commit('setLoading', false)
        }
      }
    }

    return {
      currentPage,
      postsPerPage,
      topPosts,
      // filteredPosts, // Removed as it's no longer defined
      allPosts, // Return allPosts instead
      pagination,
      loading,
      loadingMore,
      loadingTags,
      error,
      tags,
      sortedTags,
      selectedTag,
      filterByTag,
      showMobileTags,
      postsContainer,
      goToPage
    }
  }
}
</script>
